import { cn } from '@rocco/utils/cn';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import { ProductTile } from '@rocco/ui/tile-product/components/ProductTile';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import {
    TileMobileActionsSlot,
    TileOverlayWithFollowSelector,
} from '~/modules/moodboards/components';
import { RevampSectionHeading } from './RevampSectionHeading';
import {
    buildModuleProductPanel,
    buildModuleProductTile,
    useModuleProduct,
} from './revamp-module-product';

/**
 * One product tile, in the product directory's own card.
 *
 * Rocco's `ProductTile` rather than a card of this module's own, so a product
 * in an article looks like the same product in the directory. Not the
 * directory's `DirectoryProductTile` wrapper — that one carries the browse
 * page's moodboard, bulk-enquiry, library-action and view-transition
 * contexts, none of which an article body mounts.
 *
 * The card shows the product and its supplier. It carries no price and no
 * colour swatches by design — those belong to the detail page — so the
 * module's own deferred lookup supplies everything the card needs, and the
 * fields it does not read are left empty rather than invented.
 */
const CollectionTile = ({ column }: { column: ImageColumnProps }) => {
    const { product } = useModuleProduct(column);
    const model = buildModuleProductTile(column, product);

    // Rocco's tile deliberately carries no price — that is the directory's
    // call, and it holds site-wide. The article module states it underneath
    // instead, from the same `PricingDisplay` text the legacy article showed,
    // so nothing outside this module changes.
    const priceText = [product?.pricePrefix, product?.price]
        .filter(Boolean)
        .join(' ');

    // Same hover panel as the directory: Save with its label, Share and
    // the other icons along the foot. ProductTile's own control is the
    // icon-only heart in the corner, which is what this module used to show.
    const panelActions = buildModuleProductPanel(column, model);

    const { mobile } = useAppDisplayModeContext();
    const libraryImageId = column.libraryImage?.ID;
    const { width, height } = column.dimension ?? {};

    const productTile = (
        <ProductTile
            model={model}
            // Browse-revamp card, not the square legacy `product` frame.
            aspectRatio="productTall"
            section="ArticleCollection"
            hideFollowButton
            overlay={
                panelActions ? (
                    <TileOverlayWithFollowSelector {...panelActions} />
                ) : undefined
            }
        />
    );

    return (
        // `#f5f5f7` so a cut-out reads on the article ground; `#fafafa`
        // (the directory default) disappears on page white.
        <div className="relative flex flex-col [--color-clearcut-fill:#f5f5f7]">
            {libraryImageId && !mobile ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImageId}
                    currentImageUrl={column.imageUrl ?? ''}
                    displayWidth={width ?? 0}
                    displayHeight={height ?? 0}
                    columnId={column.id}
                    wasUpscaled={
                        !!column.libraryImage?.WasUpscaled ||
                        !!column.wasUpscaled
                    }
                    className="w-full"
                    controlInActionPanel
                >
                    {productTile}
                </BoostResolutionOverlay>
            ) : (
                productTile
            )}
            {panelActions ? <TileMobileActionsSlot {...panelActions} /> : null}
            {priceText ? (
                <p className="m-0 mt-1 text-sm leading-none font-medium text-[#181818] md:text-base">
                    {priceText}
                </p>
            ) : null}
        </div>
    );
};

interface RevampCollectionModuleProps {
    columns: ImageColumnProps[];
    /** Optional ruled heading, from the module's h3 title column. */
    titleHTML?: string;
}

/**
 * Single-row and two-row collection (Figma 2479:70454): product tiles under
 * the module's optional ruled heading. Only the titled presets carry a heading
 * column, so a collection built from the plain gallery presets renders as a
 * bare grid. The card is the browse-revamp `productTall` tile, not the
 * square legacy `product` frame.
 */
export const RevampCollectionModule = ({
    columns,
    titleHTML,
}: RevampCollectionModuleProps) => {
    const products = columns.filter((column) => column.imageUrl);
    if (products.length === 0) return null;

    return (
        <section className="w-full">
            <RevampSectionHeading contentHTML={titleHTML} />
            <div
                className={cn(
                    'grid grid-cols-2 gap-x-4 gap-y-10 px-4.5 md:grid-cols-4 md:gap-6 md:px-[3rem]',
                    titleHTML && 'mt-6 md:mt-[1.5rem]',
                )}
            >
                {products.map((column) => (
                    <CollectionTile key={column.id} column={column} />
                ))}
            </div>
        </section>
    );
};
